/**
 * Client Portal V2 - Quotes List Page
 * ====================================
 * Quote approvals with full commercial control.
 */

import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiClient, clientAuth } from "@/lib/clientApi";
import ClientLayout from "@/components/client/ClientLayout";
import {
  ClientStatusBadge, ClientKpiCard, ClientEmptyState, 
  ClientLoadingState, ClientErrorState
} from "@/components/client/ClientComponents";
import { 
  FileText, Search, ChevronRight, Clock, 
  CheckCircle, XCircle, AlertTriangle
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function ClientQuotesPage() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [meta, setMeta] = useState({ total: 0, page: 1, page_size: 25, total_pages: 1 });
  const [summary, setSummary] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const status = searchParams.get("status") || "";
  const search = searchParams.get("search") || "";
  const page = parseInt(searchParams.get("page") || "1", 10);

  const fetchQuotes = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (search) params.set("search", search);
      params.set("page", page.toString());
      params.set("page_size", "25");

      const res = await apiClient.get(`/api/client/quotes?${params}`);
      
      if (res.data?.success) {
        setQuotes(res.data.data || []);
        setMeta(res.data.meta || {});
        setSummary(res.data.summary || {});
      }
    } catch (err) {
      console.error("Failed to load quotes:", err);
      if (err.response?.status === 401) {
        clientAuth.logout();
        return;
      }
      setError("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  }, [status, search, page]);

  useEffect(() => {
    if (!clientAuth.isAuthenticated()) {
      navigate("/client-login");
      return;
    }
    fetchQuotes();
  }, [fetchQuotes, navigate]);

  const updateFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.set("page", "1");
    setSearchParams(params);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    updateFilter("search", formData.get("search"));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "-";
    return `£${Number(value).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
  };

  const getQuoteStatusBadge = (quote) => {
    if (quote.action_required) {
      return <Badge className="bg-orange-100 text-orange-700 ring-2 ring-orange-300">Action Required</Badge>;
    }
    if (quote.status === "Approved") {
      return <Badge className="bg-green-100 text-green-700">Approved</Badge>;
    }
    if (quote.status === "Rejected") {
      return <Badge className="bg-red-100 text-red-700">Rejected</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-700">{quote.status}</Badge>;
  };

  const awaitingCount = summary.awaiting_approval || quotes.filter(q => q.action_required).length;

  return (
    <ClientLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Quotes</h1>
        <p className="text-slate-500 mt-1">Review and approve contractor quotes</p>
      </div>

      {/* Alert Banner for Awaiting Quotes */}
      {awaitingCount > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-orange-600" />
          <span className="text-orange-800 font-medium">
            {awaitingCount} quote{awaitingCount > 1 ? "s" : ""} awaiting your approval
          </span>
          <Button
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={() => updateFilter("status", "awaiting")}
          >
            View All
          </Button>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <ClientKpiCard
          title="Total Quotes"
          value={meta.total}
          icon={FileText}
          color="teal"
        />
        <ClientKpiCard
          title="Awaiting Approval"
          value={awaitingCount}
          icon={Clock}
          color="amber"
          alert={awaitingCount > 0}
          onClick={() => updateFilter("status", "awaiting")}
        />
        <ClientKpiCard
          title="Approved"
          value={quotes.filter(q => q.status === "Approved").length}
          icon={CheckCircle}
          color="green"
          onClick={() => updateFilter("status", "approved")}
        />
        <ClientKpiCard
          title="Rejected"
          value={quotes.filter(q => q.status === "Rejected").length}
          icon={XCircle}
          color="red"
          onClick={() => updateFilter("status", "rejected")}
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                name="search"
                placeholder="Search by reference, job, contractor..."
                defaultValue={search}
                className="pl-10"
              />
            </div>
          </form>

          <Select value={status || "all"} onValueChange={(v) => updateFilter("status", v === "all" ? "" : v)}>
            <SelectTrigger className="w-full md:w-48">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="awaiting">Awaiting Approval</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>

          {(status || search) && (
            <Button
              variant="ghost"
              onClick={() => setSearchParams({})}
              className="shrink-0"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Quotes List */}
      {loading ? (
        <ClientLoadingState message="Loading quotes..." />
      ) : error ? (
        <ClientErrorState message={error} onRetry={fetchQuotes} />
      ) : quotes.length === 0 ? (
        <ClientEmptyState
          icon={FileText}
          title="No quotes found"
          description={status || search ? "Try adjusting your filters" : "No quotes have been submitted yet"}
        />
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Table Header */}
          <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            <div className="col-span-2">Quote Ref</div>
            <div className="col-span-2">Job</div>
            <div className="col-span-2">Park</div>
            <div className="col-span-2">Contractor</div>
            <div className="col-span-1">Subtotal</div>
            <div className="col-span-1">Total</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1"></div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-slate-100">
            {quotes.map((quote) => (
              <Link
                key={quote.id}
                to={`/client-v2/quotes/${quote.id}`}
                className={`block hover:bg-slate-50 transition-colors ${quote.action_required ? "bg-orange-50/50" : ""}`}
                data-testid={`quote-row-${quote.id}`}
              >
                {/* Desktop Row */}
                <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-4 items-center">
                  <div className="col-span-2">
                    <p className="font-medium text-slate-900">{quote.quote_reference}</p>
                    <p className="text-xs text-slate-500">{formatDate(quote.submitted_date)}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-slate-700 truncate">{quote.job_reference || "-"}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-slate-700 truncate">{quote.park_name}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-slate-700 truncate">{quote.contractor_name}</p>
                  </div>
                  <div className="col-span-1">
                    <p className="text-slate-600">{formatCurrency(quote.subtotal)}</p>
                  </div>
                  <div className="col-span-1">
                    <p className="font-semibold text-slate-900">{formatCurrency(quote.total_inc_vat)}</p>
                  </div>
                  <div className="col-span-1">
                    {getQuoteStatusBadge(quote)}
                  </div>
                  <div className="col-span-1 text-right">
                    <ChevronRight className="w-5 h-5 text-slate-400 inline-block" />
                  </div>
                </div>

                {/* Mobile Card */}
                <div className="md:hidden p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-slate-900">{quote.quote_reference}</p>
                      <p className="text-sm text-slate-600">{quote.contractor_name}</p>
                    </div>
                    {getQuoteStatusBadge(quote)}
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">{quote.park_name}</span>
                    <span className="font-semibold text-slate-900">{formatCurrency(quote.total_inc_vat)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {meta.total_pages > 1 && (
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
              <p className="text-sm text-slate-500">
                Page {meta.page} of {meta.total_pages} ({meta.total} quotes)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.page <= 1}
                  onClick={() => updateFilter("page", String(meta.page - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.page >= meta.total_pages}
                  onClick={() => updateFilter("page", String(meta.page + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </ClientLayout>
  );
}
